'use client';

import { motion } from 'framer-motion';
import SectionTitle from '../ui/SectionTitle';
import OfferCard from '../ui/OfferCard';
import TreeOfLife from '../ui/TreeOfLife';
import type { SoinCategory } from '@/app/lib/soins';

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: 'easeOut' as const },
  }),
};

interface ServicesProps {
  /** Catégories avec images déjà résolues côté serveur */
  categories: SoinCategory[];
}

export default function Services({ categories }: ServicesProps) {
  return (
    <section id="services" className="relative overflow-hidden py-24 bg-cream-50">
      {/* Roots of the tree of life started in the Hero */}
      <TreeOfLife part="bottom" className="bg-forest/10" />

      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
        >
          <SectionTitle
            eyebrow="Mes Soins"
            title="Une invitation au bien-être"
            subtitle="Chaque soin est soigneusement choisi pour vous offrir un moment d'exception, adapté à vos besoins."
          />
        </motion.div>

        <div className="mt-16 flex flex-col gap-16">
          {categories.map((category) => (
            <div key={category.slug}>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6 }}
                className="mb-8"
              >
                <h3 className="font-serif text-2xl md:text-3xl text-forest-dark mb-2">
                  {category.name}
                </h3>
                {category.description && (
                  <p className="font-sans text-sm text-forest/60 leading-relaxed max-w-2xl">
                    {category.description}
                  </p>
                )}
              </motion.div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.soins.map((soin, i) => (
                  <motion.div
                    key={soin.slug}
                    custom={i}
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-60px' }}
                  >
                    <OfferCard item={soin} href={`/soins/${soin.slug}`} ctaLabel="Découvrir & réserver" />
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

